import React from "react";
import { Sparkle, MapPin } from "lucide-react";
import { useGallery } from "../hooks/useSiteContent";

const defaultItems = [
  {
    src: "/assets/highlight-nasio-event.jpg",
    alt: "Denisse Troconis Aoun on stage at NAS.IO Creators Become Millionaires event",
    caption: "On stage · NAS.IO",
    location: "Mexico City",
    span: "lg:col-span-5 lg:row-span-2",
    aspect: "aspect-[3/4]",
    objectPosition: "object-top",
  },
  {
    src: "/assets/highlight-nyc-community.jpg",
    alt: "NAS.IO community gathering in New York City",
    caption: "Community dinner",
    location: "New York City",
    span: "lg:col-span-7",
    aspect: "aspect-[4/3] lg:aspect-[16/10]",
    objectPosition: "object-center",
  },
  {
    src: "/assets/highlight-nassummit-team.jpg",
    alt: "Denisse with the team at NAS Summit",
    caption: "NAS Summit · Team",
    location: "Global",
    span: "lg:col-span-7",
    aspect: "aspect-[16/9] lg:aspect-[16/10]",
    objectPosition: "object-center",
  },
];

export default function Highlights() {
  const remote = useGallery("highlights");
  const items = remote && remote.length > 0
    ? remote.map((r, i) => ({
        src: r.src,
        alt: r.alt || r.caption,
        caption: r.caption || "",
        location: r.location || "",
        span: r.span || (i === 0 ? "lg:col-span-5 lg:row-span-2" : "lg:col-span-7"),
        aspect: r.aspect || (i === 0 ? "aspect-[3/4]" : "aspect-[16/10]"),
        objectPosition: r.object_position || "object-center",
      }))
    : defaultItems;
  return (
    <section
      id="highlights"
      data-testid="highlights-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 mb-8 md:mb-12 lg:mb-16">
          <div className="col-span-12 md:col-span-3">
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
              ✦ NAS.IO era
            </span>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
              Chapter 02 · Head of partnerships
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2
              data-testid="highlights-headline"
              className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight font-light"
            >
              From stage to
              <span className="italic text-[#7FB3D5]"> strategy</span>.
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl">
              Moments from a career spent in the rooms, on the stages and
              behind the launches that build creators, founders and brands —
              from New York to LATAM.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
          {items.map((item, i) => (
            <Tile key={i} {...item} testid={`highlight-tile-${i}`} />
          ))}
        </div>

        <p
          data-testid="highlights-footnote"
          className="mt-10 md:mt-12 text-center text-[10px] uppercase tracking-[0.3em] text-white/40"
        >
          ✦ Selected highlights from collaborations with NAS.IO, NAS Summit
          &amp; the global creator community ✦
        </p>
      </div>
    </section>
  );
}

function Tile({ src, alt, caption, location, span, aspect, objectPosition, testid }) {
  return (
    <figure
      data-testid={testid}
      className={`${span} group relative overflow-hidden bg-[#0a0a0a] border border-white/10 hover:border-[#7FB3D5]/40 transition-all duration-500`}
    >
      <div className={`relative ${aspect} overflow-hidden`}>
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover ${objectPosition} transition-transform duration-[1500ms] group-hover:scale-105`}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
        <figcaption className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex items-end justify-between gap-4">
          <div>
            <p className="font-display text-lg md:text-xl text-white font-light leading-tight">
              {caption}
            </p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] flex items-center gap-1.5">
              <MapPin className="w-3 h-3" strokeWidth={1.2} />
              {location}
            </p>
          </div>
          <Sparkle
            className="w-3.5 h-3.5 text-[#7FB3D5] shrink-0"
            fill="#7FB3D5"
            strokeWidth={0.6}
          />
        </figcaption>
      </div>
    </figure>
  );
}
